import { ADDRESS_TYPES } from 'features/placeOrder/OrderAddresses.constants';
import {
  REPORT_ATTRIBUTE_IDS, NO_ANGLE_BRACKETS_REGEX, PRODUCT_IDS, PRODUCT_TYPES, INGREDIENTS,
} from 'constants/AppConstants';
import { getProductFromId, getEstimatedTotalText, stripUiAddressFlags } from 'features/order/Order.utils';
import { getSubscriptionEstimatedTotal, REGULAR_DELIVERY_PRODUCT_ID } from 'features/order/Order.constants';
import i18n from 'i18n';
import { parseDateToISOString } from 'utils/date.utils';
import { getProductType } from 'utils/app.utils';
import { isEntitledForIngredient } from 'utils/auth.utils';
import {
  CLAIM_INFO_FIELDS_NO_ANGLE_BRACKETS,
  INCLUDE_SPECIAL_INSTRUCTIONS_INCOMPATIBLE_PRODUCTS,
  WALLS_ADD_ON_IDS,
  WALLS_COMPATIBILITY_KEYS,
  WALLS_PRODUCT_IDS,
  WALLS_PRODUCT_TYPES,
  SRS_INFO_RESPONSE_ATTRIBUTES,
  MAP_POINT_ADDRESS_PRIORITIES,
} from './FinalizeOrder.contants';
import { getSubstitutionKey } from './components/substitutionsAndCustomization/SubstitutionsAndCustomization.util';

export const displayIncludeSpecialInstructions = (selectedPrimaryProductID) => (
  !INCLUDE_SPECIAL_INSTRUCTIONS_INCOMPATIBLE_PRODUCTS.includes(selectedPrimaryProductID)
);

const orderPreferencesSubstitutionReportAttribute = [
  'residential2CommercialOK',
  'accuracyWaiverOK',
  'eC3DReportOK',
  'eC2DReportOK',
  'quickSquaresToQSECOK',
  'premiumReportOK',
  'standardReportOK',
  'wallsOnlyToWallsLiteOK',
  'bidPerfectToQS',
  'fullHouseToQS',
  'bidPerfectCommercialToPremiumCommercial',
  'commercialToResidential',
  'roof3DToRoof2DOK',
  'fullHouseToRoofOnlyOK',
];

export const substitutionAndCustomizationAttribute = [
  'reportOptionsCoverCompanyCity',
  'reportOptionsCoverCompanyEmail',
  'reportOptionsCoverCompanyLogo',
  'reportOptionsCoverCompanyName',
  'reportOptionsCoverCompanyState',
  'reportOptionsCoverCompanyStreet',
  'reportOptionsCoverCompanyURL',
  'reportOptionsCoverCompanyZip',
  'reportOptionsCoverContactName',
  'reportOptionsCoverContactPhone',
  'reportOptionsCoverPrimaryColor',
  'reportOptionsCoverSubTitle',
  'reportOptionsCoverTitle',
  'reportOptionsCustomCover',
];

const getMapPointsForPlaceOrder = ({
  mapPoints, geocodedAddress, userEnteredAddress, isComplex,
}) => (
  mapPoints.map(({
    userEditedAddress, address, building, ...mapPoint
  }, index) => (
    (index === 0)
      ? ({
        ...mapPoint,
        building: building || (isComplex ? String(index + 1) : ''),
        reportAddresses: [
          stripUiAddressFlags(userEnteredAddress),
          // Adds Mapped and Verified address for first map point
          { ...stripUiAddressFlags(geocodedAddress), addressTypeId: ADDRESS_TYPES.Mapped },
          { ...stripUiAddressFlags(geocodedAddress), addressTypeId: ADDRESS_TYPES.Verified },
          // Adds UserCorrected address for first map point if user has moved the pin or edited address
          ...((mapPoint.userMovedThePin || userEditedAddress)
            ? [{ ...stripUiAddressFlags(address), addressTypeId: ADDRESS_TYPES.UserCorrected }]
            : []),
        ],

      })
      : ({
        ...mapPoint,
        building: building || (isComplex ? String(index + 1) : ''),
        reportAddresses: [
          stripUiAddressFlags(userEnteredAddress),
          { ...stripUiAddressFlags(address), addressTypeId: ADDRESS_TYPES.UserCorrected },
        ],
      })
  )));

export const estimatedCostSelector = ({
  userData: { initParameters },
  order: {
    propertyType: structureType,
    selectedMeasurementRequest: measurementRequestTypeId,
    isComplex,
  },
  orderAddresses,
  placeOrder: { promoCode, productSets },
  finalizeOrder: { userId },
}) => ({
  mapPoints: getMapPointsForPlaceOrder({ ...orderAddresses, isComplex }),
  productSets,
  promoCode,
  structureType,
  userID: userId || initParameters?.customerId,
  measurementRequestTypeId,
});

const getSubstitutionAndCustomizationAttributes = (substitutionsAndCustomizationValues, includeCustomCover) => {
  const reportAttributes = Object.entries(substitutionsAndCustomizationValues).reduce(
    (accumulator, currVal) => {
      const [key, value] = currVal;
      const attribute = {
        attributeId: value.attribute,
        value: (key.includes('reportOptionsCustomCover')) && (value.value === 0) ? '1' : String(value.value ?? ''),
      };
      // Replace substitution values to yes, no , ask
      if (orderPreferencesSubstitutionReportAttribute.includes(key)) {
        attribute.value = getSubstitutionKey(value.value).toLowerCase();
      }
      // Dont include Deliverable as it's added directly in main request
      if (!key.includes('reportOptionsDeliverable')) {
        if (substitutionAndCustomizationAttribute.includes(key)) {
          if (includeCustomCover) accumulator.push(attribute);
        } else accumulator.push(attribute);
      }
      return accumulator;
    },
    [],
  );
  return reportAttributes;
};

const getSRSCustomerInfoAttributes = (srsValidation) => {
  if (!srsValidation.srsValidationInfo || typeof srsValidation.srsValidationInfo !== 'object') {
    return [];
  }

  return Object.entries(srsValidation.srsValidationInfo).reduce((accumulator, [key, value]) => {
    if (value != null && value !== '') {
      accumulator.push({
        attributeId: parseInt(SRS_INFO_RESPONSE_ATTRIBUTES[key], 10),
        value: String(value),
      });
    }
    return accumulator;
  }, []);
};

export const placeOrderSelector = ({
  userData: {
    initParameters: { customerId },
    billingInformation: {
      hasAdministerBillingPermission,
    },
  },
  order: {
    selectedMeasurementRequest: measurementRequestTypeId,
    isComplex,
    propertyType: structureType,
  },
  srs: { srsValidation },
  orderAddresses,
  placeOrder: {
    comments,
    changesIn4Years,
    productSets,
    cardData,
    ccConsent,
    emailCC,
    bluePrintFileId,
    bluePrintId,
    promoCode,
  },
  userEntitlements,
  finalizeOrder: {
    upgradeFromReportId,
    productId,
    userId,
    claimInfo: {
      appointmentDate, dateOfLoss, renewalDate, externalReferenceId, ...claimInformationFields
    },
    substitutionsAndCustomizationValues,
    includeCustomCover,
  },
}) => ({
  mapPoints: getMapPointsForPlaceOrder({ ...orderAddresses, isComplex }),
  productSets,
  changesIn2Years: changesIn4Years,
  comments,
  promoCode,
  ...claimInformationFields,
  ...(externalReferenceId ? { externalReferenceId } : {}),
  userID: userId || customerId,
  measurementRequestTypeId,
  structureType,
  cardData,
  saveCard: !hasAdministerBillingPermission ? true : ccConsent,
  upgradeFromReportId: Number(upgradeFromReportId),
  upgradeFromProduct: productId,
  upgradeFromMso: isComplex,
  ...(substitutionsAndCustomizationValues.reportOptionsDeliverableXml
    && { includeXmlFormat: substitutionsAndCustomizationValues.reportOptionsDeliverableXml.value }),
  ...(substitutionsAndCustomizationValues.reportOptionsDeliverableRxf
    && { includeRxfFormat: substitutionsAndCustomizationValues.reportOptionsDeliverableRxf.value }),
  ...(substitutionsAndCustomizationValues.reportOptionsDeliverableDxf
    && { includeDxfFormat: substitutionsAndCustomizationValues.reportOptionsDeliverableDxf.value }),
  ...(substitutionsAndCustomizationValues.reportOptionsDeliverablePropertyOwner
    && { includePropertyOwnerReport: substitutionsAndCustomizationValues.reportOptionsDeliverablePropertyOwner.value }),
  ...(bluePrintFileId === '' ? {} : { bluePrintFileId, bluePrintId }),
  reportAttributes: [
    {
      attributeId: REPORT_ATTRIBUTE_IDS.emailCC,
      value: emailCC.join('; '),
    },
    ...(appointmentDate ? [{
      attributeId: REPORT_ATTRIBUTE_IDS.reportAppointmentDate,
      value: parseDateToISOString(appointmentDate),
    }] : []),
    ...getSubstitutionAndCustomizationAttributes(substitutionsAndCustomizationValues, includeCustomCover),
    ...getSRSCustomerInfoAttributes(srsValidation),
    ...isEntitledForIngredient(userEntitlements, INGREDIENTS.HIDE_PRICING) ? [{
      attributeId: REPORT_ATTRIBUTE_IDS.hidePriceEntitlement,
      value: String(true),
    }] : [],
  ],
  dateOfLoss: parseDateToISOString(dateOfLoss) || '',
  renewalDate: parseDateToISOString(renewalDate) || '',
});

export const paymentInfoSelector = ({
  finalizeOrder: {
    paymentInfo: {
      firstName,
      lastName,
      cardNumber,
      cardType,
      consent,
      expirationDateMonth,
      expirationDateYear,
    },
  },
}) => ({
  firstName,
  lastName,
  cardNumber,
  cardType,
  consent,
  expirationDateMonth,
  expirationDateYear,
});

export const getWallsProductType = (wallsProductId) => {
  switch (wallsProductId) {
    case WALLS_PRODUCT_IDS.WallsAddOn:
    case WALLS_PRODUCT_IDS.PremiumResidentialWallonly:
    case WALLS_PRODUCT_IDS.PremiumCommercialWallonly:
      return WALLS_PRODUCT_TYPES.Walls;

    case WALLS_PRODUCT_IDS.WallsLite:
    case WALLS_PRODUCT_IDS.WallsLiteAddOn:
      return WALLS_PRODUCT_TYPES.WallsLite;

    case WALLS_PRODUCT_IDS.WallsLiteV2:
    case WALLS_PRODUCT_IDS.WallsLiteV2AddOn:
      return WALLS_PRODUCT_TYPES.WallsLiteV2;

    case WALLS_PRODUCT_IDS.ResWallWinDoor:
    case WALLS_PRODUCT_IDS.ResWallWinDoorAddOn:
    case WALLS_PRODUCT_IDS.WallWinDoor:
      return WALLS_PRODUCT_TYPES.ResWallWinDoor;

    case WALLS_PRODUCT_IDS.ResWallsFKALite:
    case WALLS_PRODUCT_IDS.ResWallsFKALiteAddOn:
    case WALLS_PRODUCT_IDS.WallsFKALite:
      return WALLS_PRODUCT_TYPES.ResWallsFKALite;
    default: return 0;
  }
};

export const getWallsAddOnId = (roofProduct, wallsProductId) => {
  /** If selected walls product is compatible with selected roof product, return corresponding walls add on product ID for the product type */
  const productType = getWallsProductType(wallsProductId);
  if (roofProduct[WALLS_COMPATIBILITY_KEYS[productType]]) return WALLS_ADD_ON_IDS[productType];
  return null;
};

// #genai OH filter mapping — no roof compatibility gate (family PID → add-on PID only)
export const getWallsAddOnIdByProductId = (wallsProductId) => {
  const productType = getWallsProductType(wallsProductId);
  return WALLS_ADD_ON_IDS[productType] || null;
};

export const addProductToProductSets = (productSets, addOnProductIds, primaryProductId, deliveryProductId) => {
  const primaryProductType = getProductType(primaryProductId);
  const deliveryProductType = getProductType(deliveryProductId);
  productSets.push({
    addOnProductIds,
    primaryProductId,
    deliveryProductId,
    primaryProductType,
    deliveryProductType,
  });
};

// #genai: selector for the upgrade product-set decision inputs
export const upgradeProductSetSelector = ({ finalizeOrder, placeOrder }) => ({
  upgradeESX: finalizeOrder.upgradeESX,
  canAddEsxToCurrentProduct: finalizeOrder.canAddEsxToCurrentProduct,
  upgradeProductList: finalizeOrder.upgradeProductList,
  currentProductId: finalizeOrder.productId,
  productSets: placeOrder.productSets,
});

// #genai: Fail-safe — redirect to the order page when neither upgrade path is valid.
// ESX add-on flow requires canAddEsxToCurrentProduct; product upgrade flow requires an upgrade target.
export const shouldRedirectFromUpgrade = ({ upgradeESX, canAddEsxToCurrentProduct, upgradeProductList }) => {
  const isInvalidEsxAddon = upgradeESX && !canAddEsxToCurrentProduct;
  const isInvalidProductUpgrade = !upgradeESX && !upgradeProductList?.length;
  return isInvalidEsxAddon || isInvalidProductUpgrade;
};

// #genai: ESX-only addition keeps the current product and appends the ESX add-on; returns null when not applicable
export const buildEsxUpgradeProductSets = ({
  upgradeESX, canAddEsxToCurrentProduct, currentProductId, productSets,
}) => {
  const shouldAttachEsx = upgradeESX && canAddEsxToCurrentProduct && currentProductId && productSets?.[0];
  if (!shouldAttachEsx) return null;

  const existingAddOns = productSets[0].addOnProductIds ?? [];
  const nextAddOns = existingAddOns.includes(PRODUCT_IDS.ESX)
    ? existingAddOns
    : [...existingAddOns, PRODUCT_IDS.ESX];
  return [{
    ...productSets[0],
    primaryProductId: currentProductId,
    addOnProductIds: nextAddOns,
  }];
};

/**
 * Returns subscription usage metadata for the upgrade target product, or null if non-subscription.
 */
export const getUpgradeSubscriptionProduct = (productSet, primarySubscriptionProductsWithUsage) => {
  if (!productSet?.primaryProductId) return null;
  return (primarySubscriptionProductsWithUsage ?? []).find(
    (p) => Number(p.productId) === Number(productSet.primaryProductId) && p.isSubscription,
  ) ?? null;
};

/**
 * Computes formatted subscription estimated total for the upgrade flow from productSets + redux usage data.
 * Returns null when the selected product is not a subscription product (caller should use estimated-cost API).
 */
export const buildUpgradeSubscriptionEstimatedTotalText = ({
  productSet,
  primarySubscriptionProductsWithUsage,
  currencyTypeId,
}) => {
  if (!productSet?.primaryProductId) return null;

  const subscriptionProduct = getUpgradeSubscriptionProduct(productSet, primarySubscriptionProductsWithUsage);
  if (!subscriptionProduct) return null;

  const deliveryProductId = productSet.deliveryProductId ?? REGULAR_DELIVERY_PRODUCT_ID;
  const selectedOtherProducts = (productSet.addOnProductIds ?? []).reduce((acc, addOnId) => {
    acc[addOnId] = deliveryProductId;
    return acc;
  }, {});

  // Upgrade always consumes 1 unit — including Full House (order flow still uses unitBadgeCount 2).
  const total = getSubscriptionEstimatedTotal({
    subscriptionProduct: {
      ...subscriptionProduct,
      unitBadgeCount: 1,
    },
    selectedPrimaryDeliveryProduct: deliveryProductId,
    selectedOtherProducts,
    orderedUnits: 1,
  });

  return getEstimatedTotalText(total, currencyTypeId, 1);
};
export const getMapPointsForUpgrade = (upgradeDetails) => {
  const mapPoints = upgradeDetails.mapPoints.map((value) => {
    const mapPointAddress = MAP_POINT_ADDRESS_PRIORITIES
      .map((id) => value.reportAddresses.find(({ addressTypeId }) => addressTypeId === id))
      .find((obj) => obj !== undefined) || value.reportAddresses?.[0];

    return {
      userMovedThePin: value.userMovedThePin,
      building: value.building,
      address: mapPointAddress,
    };
  });
  const geocodedAddress = upgradeDetails.mapPoints[0].reportAddresses[1];
  const userEnteredAddress = upgradeDetails.mapPoints[0].reportAddresses[0];
  return { mapPoints, geocodedAddress, userEnteredAddress };
};
/**
 * Validates the input values based on the provided validators.
 *
 * @param {Object} inputValues - The input values to be validated.
 * @param {Object} validators - The validators object containing validation rules for each input field.
 * @returns {Object} - An object containing the validation errors for each input field.
 */
export const validateClaimInfoInputs = (inputValues, validators) => Object.entries(inputValues).reduce((errors, [key, value]) => {
  const { t } = i18n;
  if (
    CLAIM_INFO_FIELDS_NO_ANGLE_BRACKETS.includes(key) && !NO_ANGLE_BRACKETS_REGEX.test(value)
  ) return { ...errors, [key]: 'Angle brackets are not allowed' };

  const fieldValidator = validators[key];
  if (fieldValidator && fieldValidator.regex !== 'null') {
    const { regex, errorMessage, required } = fieldValidator;

    if (required && !value) {
      return {
        ...errors,
        [key]: t(
          'common__field_is_required',
          { field: t(`finalize_order__claim_information__${key.toLowerCase()}`) },
        ),
      };
    }

    if (regex && value) {
      if (!(new RegExp(regex).test(value))) return { ...errors, [key]: errorMessage };
    }
  }
  return { ...errors, [key]: '' };
}, {});

/**
 * Checks if an order has a carrier estimate.
 * @param {Array} productSets - The array of product sets.
 * @returns {boolean} - Returns true if the order has a carrier estimate, false otherwise.
 */
export const orderHasCarrierEstimate = (productSets) => (productSets?.some((productSet) => productSet.primaryProductId === PRODUCT_IDS.CarrierEstimate));

/**
 * Determines whether to show the insured name field based on control states and product sets.
 * @param {object} controlStates - The control states object.
 * @param {Array} productSets - The array of product sets.
 * @returns {boolean} - True if the insured name field should be shown, false otherwise.
 */
export const showInsuredNameField = (controlStates, productSets) => (
  (controlStates?.insuredName && !controlStates.insuredName.hidden)
  || orderHasCarrierEstimate(productSets)
);

/**
 * Determines whether to show the job number field based on control states and the selected primary product.
 * @param {object} controlStates - The control states object.
 * @param {object} selectedPrimaryProduct - The selected primary product object.
 * @returns {boolean} - True if the job number field should be shown, false otherwise.
 */
export const showJobNumberField = (controlStates, selectedPrimaryProduct) => (
  (controlStates?.jobNumber && !controlStates.jobNumber.hidden)
  || (selectedPrimaryProduct?.ProductType === PRODUCT_TYPES.Gutter)
);

/**
 * Determines whether to show the renewal date field based on the selected products.
 * @param {Array} productList - The list of products.
 * @param {Array} productSets - The list of product sets.
 * @returns {boolean} - True if the renewal date field should be shown, false otherwise.
 */
export const showRenewalDateField = (productList, productSets) => (
  productSets?.some(
    (productSet) => [PRODUCT_TYPES.RiskManagement, PRODUCT_TYPES.RoofGeometry]
      .includes(getProductFromId(productList, productSet.primaryProductId)?.ProductType),
  )
);

/**
 * Updates the keys of a validators object and returns a new object with the updated keys.
 * @param {Object} validators - The validators object.
 * @param {string[]} keys - The keys to update.
 * @returns {Object} - The new validators object with updated keys.
 */
export const updateValidatorFieldsKeys = (validators, keys) => {
  const fieldValidators = Object.entries(validators).reduce((newValidators, [key, value]) => ({
    ...newValidators,
    [key.toLowerCase()]: value,
  }), {});

  return keys.reduce((newValidators, key) => ({
    ...newValidators,
    [key]: fieldValidators[key.toLowerCase()],
  }), {});
};

export const getNewSKUSLAEstimatedDeliveryTimeMessage = (productId, deliveryType) => {
  switch (productId) {
    case PRODUCT_IDS.Roof:
    case PRODUCT_IDS.Roof2D:
      return {
        delivery: `${deliveryType} delivery within 9 hours`,
        deliverySubtitle: 'Typically 3 hours*',
      };
    case PRODUCT_IDS.Bid_Perfect:
      return {
        delivery: `${deliveryType} delivery within 6 hours`,
        deliverySubtitle: 'Typically 1 hour*',
      };
    case PRODUCT_IDS.WallWinDoor:
    case PRODUCT_IDS.WallsFKALite:
      return {
        delivery: `${deliveryType} delivery within 36 hours`,
        deliverySubtitle: 'Typically 12 hours*',
      };
    case PRODUCT_IDS.Full_House:
      return {
        delivery: `${deliveryType} delivery within 18 hours`,
        deliverySubtitle: 'Typically 6 hours*',
      };
    default:
      return {};
  }
};
